import { useScopedPathname } from "@-/com.npmjs.expo-router";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useLearnedGraphemesQueryOptions } from "#use-learned-graphemes-query";
import { View } from "@-/com.npmjs.react-native";
import { PrimaryControlsStripExpansionMutePushButton } from "@-/dev.improve.haniverse.routes.nav.app._components.primary-controls-strip.expansion.push-button.mute";
import { Progress } from "@-/dev.improve.haniverse.components.ui.progress";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useFindLevelContainingCharacter } from "@-/dev.improve.haniverse.routes.nav.app._lib.level.read";
import {
  useDueRowsCountQueryOptions,
  useReviewedRowsCountQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.review";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import plurCount from "@-/com.npmjs.plur-count";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

function LearnPrimaryControlsStripExpansionPushButtons() {
  return (
    <View className="flex-row items-center justify-center gap-2 py-1">
      <PrimaryControlsStripExpansionMutePushButton />
    </View>
  );
}

export function LearnPrimaryControlsStripExpansionContent() {
  // Learn's own pathname: the hidden Learn header must not re-render for other tabs' navigation.
  const pathname = useScopedPathname("/learn");
  const routeParts = new Set(pathname.split("/").filter(Boolean));
  // The deck route hosts both review and lesson cards under one path; the focused deck page
  // publishes its current card into the layout context. This header is a persistent layout
  // sibling, so it reads THAT rather than the router's global params (which bleed across mounted
  // stack entries). Slides still live under `/learn/lesson/…`.
  const [activeDeckCard] = useContextSelectors(LearnLayoutContext, (c) => c.activeDeckCard);
  const isLessonRoute =
    activeDeckCard?.kind === "lesson" ||
    (activeDeckCard === null && routeParts.has("lesson") && !routeParts.has("review"));
  const isReviewRoute =
    !isLessonRoute && (activeDeckCard?.kind === "review" || routeParts.has("review"));

  if (isReviewRoute) {
    return <LearnPrimaryControlsStripReviewProgress />;
  }
  // Lesson progress is per level, so it needs the lesson card's grapheme; a lesson route without a
  // published deck card (slides) has no level to measure.
  if (isLessonRoute && activeDeckCard !== null) {
    return <LearnPrimaryControlsStripLessonProgress morpheme={activeDeckCard.grapheme} />;
  }
  return <LearnPrimaryControlsStripPlainExpansion />;
}

function LearnPrimaryControlsStripPlainExpansion() {
  return (
    <View className="w-full px-3 pt-0.5 pb-1">
      <LearnPrimaryControlsStripExpansionPushButtons />
    </View>
  );
}

function LearnPrimaryControlsStripReviewProgress() {
  // Counts come from SQL aggregates instead of folding the full study/fsrs/review tables in JS.
  //
  // `queryKey` rotates on every write to the underlying tables (it embeds the
  // actor table change-version), so each new card flips React Query to a fresh
  // key whose `data` is `undefined` until the refetch resolves. Without
  // `keepPreviousData` the progress bar reads that `undefined` and snaps to a
  // blank/0% state for a frame before showing the new value. Keeping the prior
  // data visible during the refetch holds the bar steady across card reveals.
  const { data: dueReviewCount = 0 } = useQuery({
    ...useDueRowsCountQueryOptions(),
    placeholderData: keepPreviousData,
  });
  const { data: reviewedTodayCount = 0 } = useQuery({
    ...useReviewedRowsCountQueryOptions(),
    placeholderData: keepPreviousData,
  });

  const reviewTotal = reviewedTodayCount + dueReviewCount;
  const reviewProgressPercent = reviewTotal === 0 ? 100 : (reviewedTodayCount / reviewTotal) * 100;

  return (
    <View className="w-full px-3 pt-0.5 pb-1">
      <Progress
        className="h-1.5 rounded-full bg-muted"
        indicatorClassName="rounded-full bg-foreground/70"
        value={reviewProgressPercent}
      />
      <Text className="mt-1 text-center font-sans text-[11px] text-muted-foreground">
        {plurCount(dueReviewCount, "card")} due · {plurCount(reviewedTodayCount, "card")} cleared
        today
      </Text>
      <LearnPrimaryControlsStripExpansionPushButtons />
    </View>
  );
}

function LearnPrimaryControlsStripLessonProgress({ morpheme }: { morpheme: string }) {
  const level = useFindLevelContainingCharacter(morpheme);
  const { data: learnedFronts } = useQuery({
    ...useLearnedGraphemesQueryOptions(),
    placeholderData: keepPreviousData,
  });

  if (level === undefined || !isNonEmpty(level.characters)) {
    return <LearnPrimaryControlsStripPlainExpansion />;
  }

  const levelGraphemeList = level.characters;
  let lessonCharactersLearnedCount = 0;
  for (const front of levelGraphemeList) {
    if (learnedFronts?.has(front) === true) {
      lessonCharactersLearnedCount++;
    }
  }
  const levelGraphemeCount = arrayLength(levelGraphemeList);
  const cardsLeftInLessonCount = levelGraphemeCount - lessonCharactersLearnedCount;
  const lessonProgressPercent = (lessonCharactersLearnedCount / levelGraphemeCount) * 100;

  return (
    <View className="w-full px-3 pt-0.5 pb-1">
      <Progress
        className="h-1.5 rounded-full bg-muted"
        indicatorClassName="rounded-full bg-foreground/70"
        value={lessonProgressPercent}
      />
      <Text className="mt-1 text-center font-sans text-[11px] text-muted-foreground">
        {plurCount(cardsLeftInLessonCount, "card")} left in this lesson
      </Text>
      <LearnPrimaryControlsStripExpansionPushButtons />
    </View>
  );
}
